import { useState, useRef, useEffect } from "react";
import { Play } from "lucide-react";
import videoThumbnail from "@/assets/video-thumbnail.jpg";

const YOUTUBE_VIDEO_ID = "vnY0cSW5Des";

export const VideoSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Mount detection
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // IntersectionObserver après mount
  useEffect(() => {
    if (!isMounted || !contentRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [isMounted]);

  const handlePlay = () => {
    setIsPlaying(true);
  };

  return (
    <section
      id="video-demo"
      className="py-6 sm:py-8 lg:py-8 px-4 sm:px-6 lg:px-8"
    >
      <div
        ref={contentRef}
        className={`max-w-5xl mx-auto transition-all duration-700 ease-out ${
          isVisible 
            ? "opacity-100 translate-y-0" 
            : "opacity-0 translate-y-5"
        }`}
      >
        <div
          className="relative w-full overflow-hidden rounded-[20px] sm:rounded-[24px]"
          style={{
            aspectRatio: "16/9",
            boxShadow: "0 8px 32px -8px rgba(0, 0, 0, 0.12), 0 4px 16px -4px rgba(0, 0, 0, 0.08)",
          }}
        >
          {!isPlaying ? (
            <>
              {/* Custom video thumbnail */}
              <img
                src={videoThumbnail}
                alt="Vidéo de démonstration UNOTIF"
                className="absolute inset-0 w-full h-full object-cover"
                width={1280}
                height={720}
                loading="eager"
                decoding="async"
              />

              {/* Glass Play Button */}
              <button
                onClick={handlePlay}
                className="absolute inset-0 flex items-center justify-center z-10 group cursor-pointer"
                aria-label="Lancer la vidéo"
              >
                <div
                  className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:bg-white/30"
                  style={{
                    boxShadow: "inset 2px 2px 4px 0 rgba(255, 255, 255, 0.3), inset -2px -2px 4px 0 rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <Play
                    className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-white fill-white ml-1"
                  />
                </div>
              </button>
            </>
          ) : (
            /* YouTube Iframe - plays with sound */
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${YOUTUBE_VIDEO_ID}?autoplay=1&rel=0&modestbranding=1&showinfo=0`}
              title="Démonstration UNOTIF"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-0"
            />
          )}
        </div>
      </div>
    </section>
  );
};
